import { useState } from "react";
import { ChevronDown, Landmark, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { FaLinkedin } from "react-icons/fa6";
import { SiMastercard, SiPaypal, SiVisa } from "react-icons/si";

import Container from "../Container/Container";
import { SITE } from "../../Utils/constants";
import logoLight from "../../Assets/logo/connectify-logo.png";

import "../../Style/footer.css";

// Official Microsoft Teams Vector Icon
const MicrosoftTeamsIcon = ({ size = 18, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M19.5 7.5a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm-3.5 3h3a2 2 0 0 1 2 2v2.5a.5.5 0 0 1-.5.5h-4.5V10.5zm-5-4a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0zm-4.5 4h4a2.5 2.5 0 0 1 2.5 2.5V17a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-4a2.5 2.5 0 0 1 2.5-2.5z" />
  </svg>
);

const FOOTER_NAV_COLUMNS = [
  {
    title: "Our Data",
    links: [
      { label: "Data Accuracy", href: "/data-accuracy" },
      { label: "Data Coverage", href: "/data-coverage" },
      { label: "Data Privacy", href: "/data-privacy" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "B2B Company Data", href: "/services/b2b-company-data" },
      { label: "Data Appending", href: "/services/data-appending" },
      { label: "B2B Appointment", href: "/services/b2b-appointment" },
      { label: "Lead Generation", href: "/services/lead-generation" },
    ],
  },
];

const COMPLIANCE_BADGES = [
  { code: "GDPR", sub: "COMPLIANT" },
  { code: "CCPA", sub: "COMPLIANT" },
  { code: "CASL", sub: "COMPLIANT" },
  { code: "ACMA", sub: "COMPLIANT" },
  { code: "EDPS", sub: "COMPLIANT" },
  { code: "CAN-SPAM", sub: "ACT REG." },
];

function Footer() {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (title) => {
    setOpenSection((prev) => (prev === title ? null : title));
  };

  const handleLinkClick = (href) => (e) => {
    if (href.startsWith("http") || href.startsWith("tel:") || href.startsWith("mailto:")) {
      return;
    }

    e.preventDefault();

    // 1. In-page smooth scroll if on Home
    if (href.startsWith("/#") && (window.location.pathname === "/" || window.location.pathname === "")) {
      const targetId = href.replace("/#", "");
      const elem = document.getElementById(targetId);
      if (elem) {
        elem.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }

    // 2. Cross-page anchor navigation (navigate to / then scroll)
    if (href.startsWith("/#") && window.location.pathname !== "/") {
      window.history.pushState({}, "", "/");
      window.dispatchEvent(new Event("popstate"));
      setTimeout(() => {
        const targetId = href.replace("/#", "");
        const elem = document.getElementById(targetId);
        if (elem) {
          elem.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
      return;
    }

    // 3. Client-side route change
    if (href.startsWith("/")) {
      if (window.location.pathname === href) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        return;
      }

      window.history.pushState({}, "", href);
      window.dispatchEvent(new Event("popstate"));
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  };

  return (
    <footer className="footer">
      <Container>
        <div className="footer__grid">
          {/* 1. Brand & Compliance Column */}
          <div className="footer__brand">
            <a href="/" onClick={handleLinkClick("/")} className="footer__brand-link">
              <img src={logoLight} alt={SITE.name} className="footer__logo" />
            </a>

            <p className="footer__description">
              We are the best consultant for your B2B marketing campaigns and specialize in
              arranging personalized business contacts with all important decision-makers worldwide
              in all industries.
            </p>

            {/* Our Compliance Badges Strip */}
            <div className="footer__compliance">
              <span className="footer__compliance-title">Our Compliance :</span>
              <div className="footer__compliance-grid">
                {COMPLIANCE_BADGES.map((b) => (
                  <div key={b.code} className="compliance-card">
                    <ShieldCheck size={16} className="compliance-card__icon" />
                    <span className="compliance-card__code">{b.code}</span>
                    <span className="compliance-card__sub">{b.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 2. Our Data & Services Columns */}
          {FOOTER_NAV_COLUMNS.map((column) => {
            const isOpen = openSection === column.title;
            return (
              <div
                key={column.title}
                className={`footer__column ${isOpen ? "footer__column--open" : ""}`}
              >
                <button
                  type="button"
                  className="footer__column-trigger"
                  onClick={() => toggleSection(column.title)}
                  aria-expanded={isOpen}
                >
                  <span>{column.title}</span>
                  <ChevronDown size={18} className="footer__chevron" />
                </button>

                <ul className="footer__column-list">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      <a href={link.href} onClick={handleLinkClick(link.href)}>
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* 3. Headquarters Column */}
          <div
            className={`footer__column footer__headquater ${
              openSection === "Headquarters" ? "footer__column--open" : ""
            }`}
          >
            <button
              type="button"
              className="footer__column-trigger"
              onClick={() => toggleSection("Headquarters")}
              aria-expanded={openSection === "Headquarters"}
            >
              <span>Headquarters</span>
              <ChevronDown size={18} className="footer__chevron" />
            </button>

            <div className="footer__column-list footer__headquater-content">
              <div className="footer__contact-entry">
                <MapPin size={16} className="contact-icon" />
                <span>1000 Brickell Ave Ste, Miami, FL 33131</span>
              </div>

              <div className="footer__contact-entry">
                <Mail size={16} className="contact-icon" />
                <span>
                  Email:{" "}
                  <a href={`mailto:${SITE.email || "conectify-tech@gmail.com"}`}>
                    {SITE.email || "conectify-tech@gmail.com"}
                  </a>
                </span>
              </div>

              <div className="footer__contact-entry">
                <Phone size={16} className="contact-icon" />
                <span>
                  Contact:{" "}
                  <a href={`tel:${(SITE.phone || "+1 786 776 2152").replace(/[^+\d]/g, "")}`}>
                    {SITE.phone || "+1 786 776 2152"}
                  </a>
                </span>
              </div>

              {/* Social / Direct Connect Badges */}
<div className="footer__socials-strip footer__socials-strip--labeled">
  {/* LinkedIn Button */}
  <a
    href="https://linkedin.com"
    target="_blank"
    rel="noopener noreferrer"
    className="connect-badge connect-badge--linkedin"
    aria-label="Connect on LinkedIn"
  >
    <FaLinkedin size={16} className="connect-badge__icon" />
    <span className="connect-badge__text">LinkedIn</span>
  </a>

  {/* Microsoft Teams Direct Chat Button */}
  <a
    href="https://teams.microsoft.com/l/chat/0/0?users=conectify-tech@gmail.com"
    target="_blank"
    rel="noopener noreferrer"
    className="connect-badge connect-badge--teams"
    aria-label="Direct Chat on Microsoft Teams"
  >
    <MicrosoftTeamsIcon size={16} className="connect-badge__icon" />
    <span className="connect-badge__text">Teams Chat</span>
    <span className="connect-badge__pulse" title="Online for inquiries" />
  </a>
</div>

              {/* We Accept Payment Icons */}
              <div className="footer__payments">
                <span className="footer__payments-lbl">We Accept:</span>
                <div className="footer__payments-grid">
                  <span className="payment-chip" title="Visa">
                    <SiVisa size={24} />
                  </span>
                  <span className="payment-chip" title="Mastercard">
                    <SiMastercard size={20} />
                  </span>
                  <span className="payment-chip" title="Wire Transfer">
                    <Landmark size={18} />
                  </span>
                  <span className="payment-chip" title="PayPal">
                    <SiPaypal size={18} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Legal Navigation Bar */}
        <div className="footer__bottom">
          <p className="footer__copyright">
            © Copyright {new Date().getFullYear()} | {SITE.name || "Conectify Tech"} | All Rights Reserved.
          </p>
          <nav className="footer__bottom-links" aria-label="Legal">
            <a href="/terms" onClick={handleLinkClick("/terms")}> 
              Terms and Conditions
            </a>
            <a href="/privacy-policy" onClick={handleLinkClick("/privacy-policy")}>
              Privacy Policy
            </a>
            <a href="/cookies" onClick={handleLinkClick("/cookies")}>
              Cookies Policy
            </a>
            <a
              href="https://clickbydigital.in/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Design &amp; Developed by ClickByDigital
            </a>
          </nav>
        </div>
      </Container>
    </footer>
  );
}

export default Footer;